import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Grid,
  Card,
  Typography,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Box,
  Alert,
  Snackbar,
} from "@mui/material";
import { Add as AddIcon, Circle as CircleIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { Link } from "react-router-dom";

// Import components
import TicketTable from "./components/TicketTable";
import TicketToolbar from "./components/TicketToolbar";
import CreateTicketDialog from "./components/CreateTicketDialog";

// Import GraphQL
import { GET_MY_TICKETS } from "./graphql/queries";
import { DELETE_TICKETS } from "./graphql/mutations";

// Import utils
import { hasPermission } from "../../../utils/permissionUtils";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const ChatContainer = styled(Grid)`
  width: 100%;
  min-height: 75vh;
`;

const ChatSidebar = styled(Grid)`
  border-right: 1px solid
    ${(props) => props.theme?.palette?.divider || "#e0e0e0"};
`;

const SidebarMenuItem = styled(ListItem)`
  cursor: pointer;
  padding: ${(props) => props.theme?.spacing?.(2, 3) || "16px 24px"};
  border-left: 3px solid transparent;
  transition: all 0.2s;

  ${(props) =>
    props.active &&
    `
    border-left-color: ${props.theme?.palette?.primary?.main || "#1976d2"};
    background-color: ${
      props.theme?.palette?.action?.selected || "rgba(0, 0, 0, 0.08)"
    };
  `}

  &:hover {
    background-color: ${(props) =>
      props.theme?.palette?.action?.hover || "rgba(0, 0, 0, 0.04)"};
  }
`;

function SupportTickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get spaceId from URL or context
  const spaceId = searchParams.get("spaceId");

  // State
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "All"
  );
  const [priorityFilter, setPriorityFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Permissions
  const permissions = JSON.parse(localStorage.getItem("rbacPermissions")) || [];
  const canCreateTickets = hasPermission("CREATE_SUPPORT_TICKETS");
  const canDeleteTickets = hasPermission("UPDATE_SUPPORT_TICKETS"); // Using UPDATE permission for delete

  // GraphQL Query
  const { data, loading, error, refetch } = useQuery(GET_MY_TICKETS, {
    variables: {
      spaceId,
      status: statusFilter === "All" ? null : statusFilter,
      priority: priorityFilter || null,
      search: searchQuery || null,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    fetchPolicy: "network-only",
  });

  // Delete mutation
  const [deleteTicketsMutation, { loading: deleteLoading }] = useMutation(
    DELETE_TICKETS,
    {
      onCompleted: (data) => {
        setSnackbar({
          open: true,
          message: `Successfully deleted ${data.deleteTickets.deletedCount} ticket(s)`,
          severity: "success",
        });
        setSelected([]);
        setOpenDeleteDialog(false);
        refetch();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error deleting tickets: ${error.message}`,
          severity: "error",
        });
      },
    }
  );

  const tickets = data?.myTickets?.tickets || [];
  const totalCount = data?.myTickets?.total || 0;

  // Handlers
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPage(0);
    setSearchParams({ ...Object.fromEntries(searchParams), status });
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(0);
  };

  const handlePriorityChange = (priority) => {
    setPriorityFilter(priority);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = tickets.map((ticket) => ticket.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelect = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (ticket) => {
    navigate(`/widget/support-ticket/${ticket.id}`);
  };

  const handleDeleteTickets = () => {
    if (selected.length === 0) return;
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteTicketsMutation({
      variables: {
        ticketIds: selected,
      },
    });
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = [
      "Ticket #",
      "Subject",
      "Status",
      "Priority",
      "Assigned To",
      "Customer",
      "Created",
      "Updated",
    ];

    const rows = tickets.map((ticket) => [
      ticket.ticketNumber,
      ticket.subject,
      ticket.status,
      ticket.priority,
      ticket.assignedTo?.name || "Unassigned",
      ticket.customerName || "-",
      new Date(ticket.createdAt).toLocaleString(),
      new Date(ticket.updatedAt).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `tickets_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyPublicUrl = () => {
    if (!spaceId) {
      setSnackbar({
        open: true,
        message: "Space ID not found. Cannot generate public URL.",
        severity: "error",
      });
      return;
    }

    const publicUrl = `${window.location.origin}/support-ticket/${spaceId}`;

    navigator.clipboard
      .writeText(publicUrl)
      .then(() => {
        setSnackbar({
          open: true,
          message: "Public ticket URL copied to clipboard!",
          severity: "success",
        });
      })
      .catch((err) => {
        console.error("Failed to copy URL:", err);
        setSnackbar({
          open: true,
          message: `Failed to copy URL: ${err.message}`,
          severity: "error",
        });
      });
  };

  // Status counts (you could fetch these separately or calculate from data)
  const statusCounts = {
    All: totalCount,
    Open: 0,
    Pending: 0,
    "In Progress": 0,
    Resolved: 0,
    Closed: 0,
  };

  // Calculate counts from current data (this is approximate, ideally from backend)
  tickets.forEach((ticket) => {
    if (statusCounts.hasOwnProperty(ticket.status)) {
      statusCounts[ticket.status]++;
    }
  });

  return (
    <React.Fragment>
      <Helmet title="Support Tickets" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Support Tickets
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/widget/dashboard">
              Dashboard
            </Link>
            <Typography>Support Tickets</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          {canCreateTickets && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenCreateDialog(true)}
              startIcon={<AddIcon />}
            >
              New Ticket
            </Button>
          )}
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <ChatContainer container component={Card}>
            {/* Sidebar with status filters */}
            <ChatSidebar item xs={12} md={2}>
              <List>
                {Object.keys(statusCounts).map((status) => (
                  <SidebarMenuItem
                    key={status}
                    active={statusFilter === status ? 1 : 0}
                    onClick={() => handleStatusFilterChange(status)}
                  >
                    <ListItemIcon>
                      <CircleIcon
                        fontSize="small"
                        color={statusFilter === status ? "primary" : "disabled"}
                      />
                    </ListItemIcon>
                    <ListItemText primary={status} />
                    {statusCounts[status] > 0 && (
                      <Badge
                        badgeContent={statusCounts[status]}
                        color="primary"
                      />
                    )}
                  </SidebarMenuItem>
                ))}
              </List>
            </ChatSidebar>

            {/* Main content area */}
            <Grid item xs={12} md={10}>
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="400px"
                >
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box p={3}>
                  <Alert severity="error">
                    Error loading tickets: {error.message}
                  </Alert>
                </Box>
              ) : (
                <>
                  <TicketToolbar
                    numSelected={selected.length}
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    priorityFilter={priorityFilter}
                    onPriorityChange={handlePriorityChange}
                    onDelete={
                      canDeleteTickets ? handleDeleteTickets : undefined
                    }
                    onExportCSV={handleExportCSV}
                    spaceId={spaceId}
                    onCopyUrl={handleCopyPublicUrl}
                  />
                  <TicketTable
                    tickets={tickets}
                    selected={selected}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onSelectAllClick={handleSelectAllClick}
                    onSelect={handleSelect}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    onRowClick={handleRowClick}
                  />
                </>
              )}
            </Grid>
          </ChatContainer>
        </Grid>
      </Grid>

      {/* Create Ticket Dialog */}
      <CreateTicketDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSuccess={() => {
          setOpenCreateDialog(false);
          setSnackbar({
            open: true,
            message: "Ticket created successfully",
            severity: "success",
          });
          refetch();
        }}
        spaceId={spaceId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Tickets</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} ticket(s)? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default SupportTickets;
